import { usEvidenceNow } from '@/lib/polymarketUs/timeEvidence';
import { usPlayerPanelGame } from '@/views/ValueDashboard/us/usPlayerPanelGame';
import { useMemo, useState } from 'react';
import { usPlayerTarget } from '@/lib/polymarketUs/playerTarget';
import { usdToCents } from '@/lib/polymarketUs/gameMapping';
import { useSharedNow } from '@/lib/infra/sharedClock';
import type { GameRow, TradeTicketContext } from '../ValueDashboard/types';
import type { UsPlayerCatalogue } from './usPlayerCatalogue';
import { useUsPlayerQuotes } from './usPlayerQuotes';
import { usStatLabel } from '@/lib/polymarketUs/statLabels';
import { usCatalogueCount, usOpenTitle } from './usPropsCopy';

const PAGE_SIZE = 20;

/** Independent US contracts inside the existing Player Props section. No
 * copied K/P book, integer-rung transform, one-click dispatch or accounting. */
export function UsPlayerProps({ game, catalogue, onSelect }: {
  game: GameRow; catalogue: UsPlayerCatalogue; onSelect(ctx: TradeTicketContext): void;
}) {
  const [stat, setStat] = useState('');
  const [player, setPlayer] = useState('');
  const [page, setPage] = useState(0);
  const now = useSharedNow(1000, catalogue.markets.length > 0);
  // Ordered by the name the trader reads, not the slug.
  const stats = useMemo(() => [...new Set(catalogue.markets.map(m => m.playerProp!.stat))]
    .sort((a, b) => usStatLabel(a).localeCompare(usStatLabel(b))), [catalogue.markets]);
  const players = useMemo(() => [...new Set(catalogue.markets.filter(m => !stat || m.playerProp!.stat === stat)
    .map(m => m.playerProp!.player))].sort(), [catalogue.markets, stat]);
  const rows = useMemo(() => catalogue.markets.filter(m => (!stat || m.playerProp!.stat === stat)
    && (!player || m.playerProp!.player === player)), [catalogue.markets, stat, player]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const effectivePage = Math.min(page, pages - 1);
  const visibleRows = useMemo(() => rows.slice(effectivePage * PAGE_SIZE, (effectivePage + 1) * PAGE_SIZE), [rows, effectivePage]);
  const quotes = useUsPlayerQuotes(visibleRows, catalogue.status === 'ready');
  return <div className="border-border border-t p-3" data-testid="us-player-props">
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <h3 className="text-sm font-semibold">Polymarket US player props</h3>
      <select aria-label="US player statistic" className="bg-background border-border max-w-[240px] rounded border px-2 py-1 text-xs"
        value={stat} onChange={e => { setStat(e.target.value); setPlayer(''); setPage(0); }}>
        <option value="">All statistics</option>{stats.map(s => <option key={s} value={s}>{usStatLabel(s)}</option>)}
      </select>
      <select aria-label="US player" className="bg-background border-border max-w-[240px] rounded border px-2 py-1 text-xs"
        value={player} onChange={e => { setPlayer(e.target.value); setPage(0); }}>
        <option value="">All players</option>{players.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
      <span className="text-muted-foreground text-xs">{usCatalogueCount(catalogue, rows.length)}</span>
    </div>
    {catalogue.status !== 'ready' ? <p className="text-muted-foreground text-xs">{catalogue.status === 'loading'
      ? 'Loading US player contracts…' : 'US player catalogue is unavailable.'}</p>
      : rows.length === 0 ? <p className="text-muted-foreground text-xs">No supported US player contracts are listed for this selection.</p>
      : <div className="overflow-x-auto"><table className="w-full min-w-[420px] table-fixed text-xs">
        <thead className="text-muted-foreground"><tr><th className="text-left">Contract</th><th className="w-24">YES</th><th className="w-24">NO</th></tr></thead>
        <tbody>{visibleRows.map(m => {
          const target = usPlayerTarget(m)!;
          const quote = quotes.find(q => q.marketSlug === m.marketSlug);
          const quoteNow = usEvidenceNow(quote?.timeEvidence, now);
          const current = quote !== undefined && quote.classification.expiresAtMs > quoteNow && quote.book.state === 'current'
            && quote.book.expiresAtMs !== null && quote.book.expiresAtMs > quoteNow;
          const statLabel = usStatLabel(target.metadata.stat);
          return <tr key={m.marketSlug} className="border-border border-t align-top">
            <td className="py-2 pr-2 [overflow-wrap:anywhere]">
              <div className="font-semibold">{m.title ?? `${target.metadata.player} · ${statLabel}`}</div>
              <div className="text-muted-foreground text-[11px]">{target.metadata.playerTeam ?? 'Team unavailable'} · {statLabel}
                {m.line === null ? '' : ` · Line ${m.line}`}</div>
            </td>
            {(['yes', 'no'] as const).map(outcome => {
              const price = outcome === 'yes' ? quote?.book.yesBidUsd : quote?.book.noBidUsd;
              return <td key={outcome} className="py-2 text-center">
                <button type="button" className="border-border hover:bg-accent w-[5.5rem] rounded border px-1 py-1"
                  aria-label={`Open ${target.metadata.player} ${statLabel}${m.line === null ? '' : ` line ${m.line}`} ${outcome.toUpperCase()} on Polymarket US`}
                  title={usOpenTitle(outcome)}
                  onClick={() => onSelect({ game: usPlayerPanelGame(game, target, m.title), teamSide: 'away',
                    venue: 'polymarket', initialOrderSide: outcome, postOnly: false })}>
                  {outcome.toUpperCase()}<span className="block whitespace-nowrap font-mono text-muted-foreground">{current && price != null ? `${usdToCents(price)}¢ bid` : '—'}</span>
                </button>
              </td>;
            })}
          </tr>;
        })}</tbody>
      </table></div>}
    {pages > 1 && <div className="mt-2 flex items-center justify-end gap-2 text-xs">
      <button type="button" disabled={effectivePage === 0} onClick={() => setPage(effectivePage - 1)} className="disabled:opacity-40">Previous US contracts</button>
      <span>{effectivePage + 1} / {pages}</span>
      <button type="button" disabled={effectivePage + 1 >= pages} onClick={() => setPage(effectivePage + 1)} className="disabled:opacity-40">Next US contracts</button>
    </div>}
  </div>;
}
